// Value renderers. One formatter each, so every screen shows money, quantities and dates
// the same way: "R 1 234.50", "2.5 kg", "27 Sep", 24-hour times (number style: D-022).

import type { ComponentProps } from 'react';
import { cn } from '../lib/cn.js';
import { formatDate, formatDateTime, formatMoney, formatQty } from '../lib/format.js';

export interface MoneyProps extends Omit<ComponentProps<'span'>, 'children'> {
  /** Integer cents. */
  cents: number;
  /** Colour negative amounts as a loss (the minus sign is always shown). */
  signed?: boolean;
}

export function Money({ cents, signed = false, className, ...props }: MoneyProps) {
  return (
    <span
      className={cn(
        'whitespace-nowrap tabular-nums',
        signed && cents < 0 && 'text-state-danger',
        className,
      )}
      {...props}
    >
      {formatMoney(cents)}
    </span>
  );
}

/**
 * How a unit code reads next to a number, singular and plural. `ea` (counted) has no word:
 * "2", not "2 ea". Codes not listed show as they are.
 */
const UNIT_DISPLAY: Record<string, { one: string; many: string; name: string }> = {
  ea: { one: '', many: '', name: 'each (counted)' },
  cup: { one: 'cup', many: 'cups', name: 'cups' },
  l: { one: 'L', many: 'L', name: 'litres (L)' },
  ml: { one: 'ml', many: 'ml', name: 'millilitres (ml)' },
  kg: { one: 'kg', many: 'kg', name: 'kilograms (kg)' },
  g: { one: 'g', many: 'g', name: 'grams (g)' },
};

/** A unit as it reads after `value` ("cups" after 2, "cup" after 1, nothing for `ea`). */
export function unitText(unit: string, value?: string): string {
  const d = UNIT_DISPLAY[unit.trim().toLowerCase()];
  if (!d) return unit;
  // Exactly one reads singular (1, 1.0, 1.000); anything else plural.
  const one = value !== undefined && /^\+?0*1(\.0*)?$/.test(value.trim());
  return one ? d.one : d.many;
}

/** A unit on its own, as a label ("each (counted)", "kilograms (kg)"). */
export function unitName(unit: string): string {
  return UNIT_DISPLAY[unit.trim().toLowerCase()]?.name ?? unit;
}

/**
 * A decimal-string quantity with its unit as people write it: "2", "2 cups", "1 cup",
 * "0.45 kg", "1.5 L". The one way quantities render (tables, sentences, warnings).
 */
export function formatQuantity(value: string, unit?: string, maxDecimals?: number): string {
  const word = unit === undefined ? '' : unitText(unit, value);
  return word === ''
    ? formatQty(value, undefined, maxDecimals)
    : formatQty(value, word, maxDecimals);
}

export interface QtyProps extends Omit<ComponentProps<'span'>, 'children'> {
  /** Decimal string (never a float). */
  value: string;
  unit?: string;
  maxDecimals?: number;
}

export function Qty({ value, unit, maxDecimals, className, ...props }: QtyProps) {
  return (
    <span className={cn('whitespace-nowrap tabular-nums', className)} {...props}>
      {formatQuantity(value, unit, maxDecimals)}
    </span>
  );
}

export interface DateTextProps extends Omit<ComponentProps<'time'>, 'children' | 'dateTime'> {
  value: Date | string;
  withTime?: boolean;
  timeZone?: string;
}

export function DateText({
  value,
  withTime = false,
  timeZone,
  className,
  ...props
}: DateTextProps) {
  const iso = new Date(value).toISOString();
  return (
    <time
      dateTime={iso}
      title={formatDateTime(value, { timeZone })}
      className={cn('whitespace-nowrap tabular-nums', className)}
      {...props}
    >
      {withTime ? formatDateTime(value, { timeZone }) : formatDate(value, { timeZone })}
    </time>
  );
}
